import { useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Button, Form, Input, InputNumber, Modal, Tooltip, Typography } from "antd"
import { notifications } from "notifications"
import { IRedeemLimits } from "../api/redeem-limits.call"
import { MarketplaceCohorts } from "./marketplace-cohorts"

const { Title, Text } = Typography

const PASSWORD_ENV = process.env.REACT_APP_REDEEM_LIMITS_PASSWORD || ""
const SESSION_KEY = "isRedeemLimitsUnlocked"


const convertUTCHourToCentral = (utcHour?: number) => {
  if (utcHour === undefined || utcHour < 0 || utcHour > 23) return ""

  const utcDate = new Date()
  utcDate.setUTCHours(utcHour, 0, 0, 0)

  return utcDate.toLocaleString("en-US", {
    timeZone: "America/Chicago",
    hour: "2-digit",
    hour12: false,
  })
}

export const RedeemLimits = observer(() => {
  const { dataStore: { redeemLimitsStore } } = useStore()
  const [form] = Form.useForm()
  const [centralHour, setCentralHour] = useState<string>("")
  const [passwordInput, setPasswordInput] = useState("")
  const [passwordModalOpen, setPasswordModalOpen] = useState(false)
  const [passwordVerified, setPasswordVerified] = useState(
    sessionStorage.getItem(SESSION_KEY) === "true",
  )


  useEffect(() => {
    redeemLimitsStore.getData().then(data => {
      if (data) {
        form.setFieldsValue(data)
        setCentralHour(convertUTCHourToCentral(data.utcHoursForMarketplaceRedemptionLimitReset))
      }
    })
  }, [redeemLimitsStore, form])


  const onFinishHandler = async(values: IRedeemLimits) => {
    if (!passwordVerified) {
      setPasswordModalOpen(true)

      return
    }
    redeemLimitsStore.funcAfterSuccess = async() => {
      notifications.success("Saved successfully")
      await redeemLimitsStore.getData()
    }
    await redeemLimitsStore.update(values)
  }

  const handlePasswordSubmit = () => {
    const ok = passwordInput.trim() === String(PASSWORD_ENV)
    if (ok) {
      setPasswordVerified(true)
      sessionStorage.setItem(SESSION_KEY, "true")
      setPasswordInput("")
      setPasswordModalOpen(false)
      form.submit()
    } else {
      notifications.error("Incorrect password")
    }
  }

  return (
    <>
      <Form form={form} onFinish={onFinishHandler}>
        <Title level={2}>Redeem Limits</Title>
        <Form.Item
          name="maxUsdForRedeemDailyGlobal"
          label="Max USD for Redeem (Daily, Global)"
        >
          <InputNumber
            min={0}
            step={0.01}
            formatter={value => `$ ${value}`}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            parser={((value: string) => parseFloat(value?.replace(/\$\s?/, "") || "0") || 0) as any}
          />
        </Form.Item>
        <Form.Item
          name="maxUsdForRedeemDailyPerUser"
          label="Max USD for Redeem (Daily, Per User)"
        >
          <InputNumber
            min={0}
            step={0.01}
            formatter={value => `$ ${value}`}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            parser={((value: string) => parseFloat(value?.replace(/\$\s?/, "") || "0") || 0) as any}
          />
        </Form.Item>
        <Form.Item
          name="numberOfUsersDailyRedemptionAttempts"
          label="Max Daily Redemption Attempts (Per User)"
        >
          <InputNumber min={1} />
        </Form.Item>
        <Form.Item
          name="utcHoursForMarketplaceRedemptionLimitReset"
          label={
            <Tooltip title="UTC hour when the daily redemption limits reset (0–23)">
            UTC Hour for Redemption Limit Reset
            </Tooltip>
          }
        >
          <InputNumber
            min={0}
            max={23}
            placeholder="0-23"
            onChange={value => setCentralHour(convertUTCHourToCentral(value ?? undefined))}
          />
        </Form.Item>

        {centralHour && (
          <div style={{ marginTop: -12, marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
            This corresponds to {centralHour}:00 US Central Time
            </Text>
          </div>
        )}

        <Button type="primary" htmlType="submit">
        Save
        </Button>
      </Form>
      <Modal
        open={passwordModalOpen}
        title="Password protection"
        onCancel={() => {
          setPasswordModalOpen(false)
          setPasswordInput("")
        }}
        footer={null}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <p>Please enter the password to save redeem limits:</p>
          <Input.Password
            value={passwordInput}
            onChange={e => setPasswordInput(e.target.value)}
            placeholder="Enter password"
            autoComplete="new-password"
          />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              onClick={() => {
                setPasswordModalOpen(false)
                setPasswordInput("")
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              disabled={passwordInput.trim().length === 0}
              onClick={handlePasswordSubmit}
            >
              Submit
            </Button>
          </div>
        </div>
      </Modal>

      <MarketplaceCohorts />
    </>
  )
})
